import React, { useEffect, useMemo, useState } from 'react';
import { addDays, format, startOfWeek } from 'date-fns';
import type { DataMode } from '@/contexts/DataSourceContext';
import type { Company } from '@/features/multi-company/types/company.types';
import type { Employee, Timesheet, TimesheetEntry } from '@/types/timesheet';

const EMPTY_ENTRIES: TimesheetEntry[] = [];

/** Navigation state for drilldown from reports. */
export type TimesheetDrilldownState = {
  employeeId: number;
  weekEnding: Date;
};

export interface UseTimesheetWeekStateParams {
  navigationState?: TimesheetDrilldownState;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  screenMode: DataMode;
  targetEmployeeId: number;
  currentEmployee: Employee;
  /** The employee a new (unsaved) timesheet is created for; null when the target cannot be resolved. */
  targetEmployee?: Employee | null;
  employees: Employee[];
  timesheets: Timesheet[];
  currentCompany: Company | null | undefined;
  isViewingOtherResource: boolean;
  refreshEmployeeTimesheets: (employeeId: number) => void | Promise<void>;
}

export interface UseTimesheetWeekStateResult {
  currentWeekStart: Date;
  setCurrentWeekStart: React.Dispatch<React.SetStateAction<Date>>;
  currentTimesheet: Timesheet;
  viewedResourceName: string | null;
  /** True while the employee's timesheets are being re-fetched on navigation (live mode). */
  isRefreshingTimesheets: boolean;
}

/** The week being viewed and the timesheet (existing or blank) that belongs to it. */
export function useTimesheetWeekState({
  navigationState,
  weekStartDay,
  screenMode,
  targetEmployeeId,
  currentEmployee,
  targetEmployee,
  employees,
  timesheets,
  currentCompany,
  isViewingOtherResource,
  refreshEmployeeTimesheets,
}: UseTimesheetWeekStateParams): UseTimesheetWeekStateResult {
  // Initialize week based on selection or default to current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    navigationState?.weekEnding
      ? startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay })
      : startOfWeek(new Date(), { weekStartsOn: weekStartDay })
  );

  // Update current week when selection changes (e.g., navigating from Action Required card or Submission Consistency)
  useEffect(() => {
    if (navigationState?.weekEnding) {
      setCurrentWeekStart(startOfWeek(navigationState.weekEnding, { weekStartsOn: weekStartDay }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState?.weekEnding]);

  // Recalculate current week start when weekStartDay setting changes
  useEffect(() => {
    setCurrentWeekStart(prev => {
      const newStart = startOfWeek(prev, { weekStartsOn: weekStartDay });
      // Only update if the week start actually changed to avoid unnecessary re-renders
      return newStart.getTime() !== prev.getTime() ? newStart : prev;
    });
  }, [weekStartDay]);

  // In live mode, refresh the employee's timesheets from the API to pick up
  // status changes that happened after the initial data load (e.g., a manager
  // rejected a timesheet while the employee was on another page).
  // This covers both direct navigation (e.g., sidebar click) and drilldown
  // navigation (e.g., from the Action Required card on the homepage).
  const [isRefreshingTimesheets, setIsRefreshingTimesheets] = useState(screenMode === 'live');
  useEffect(() => {
    if (screenMode !== 'live') {
      setIsRefreshingTimesheets(false);
      return;
    }
    let cancelled = false;
    setIsRefreshingTimesheets(true);
    Promise.resolve(refreshEmployeeTimesheets(targetEmployeeId)).finally(() => {
      if (!cancelled) setIsRefreshingTimesheets(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState?.employeeId, navigationState?.weekEnding, screenMode]);

  // Get or create timesheet for current week.
  // Filter by active company to ensure only the correct company's timesheet is shown.
  // Uses fuzzy week matching: a timesheet belongs to this week if its weekEnding falls
  // anywhere within the 7-day window [currentWeekStart, currentWeekStart + 6].
  // This prevents lookup failures when stored dates have been shifted by timezone drift.
  const currentTimesheet = useMemo(() => {
    const weekEnding = addDays(currentWeekStart, 6);
    const weekStartStr = format(currentWeekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnding, 'yyyy-MM-dd');

    const existing = timesheets.find(
      ts => ts.employeeId === targetEmployeeId &&
      format(ts.weekEnding, 'yyyy-MM-dd') >= weekStartStr &&
      format(ts.weekEnding, 'yyyy-MM-dd') <= weekEndStr &&
      (!currentCompany?.id || !ts.companyId || ts.companyId === currentCompany.id)
    );

    if (existing) {
      return existing;
    }

    // Fallback: a new timesheet belongs to the target employee, never silently to the
    // logged-in user when the target cannot be resolved
    let owner: Employee | null = null;
    if (targetEmployee?.id === targetEmployeeId) owner = targetEmployee;
    else if (targetEmployeeId === currentEmployee.id) owner = currentEmployee;
    return {
      id: 0,
      employeeId: targetEmployeeId,
      employeeName: owner?.name ?? '',
      weekEnding,
      status: 'draft' as const,
      entries: EMPTY_ENTRIES,
      companyId: currentCompany?.id,
    };
  }, [currentWeekStart, timesheets, targetEmployeeId, currentEmployee, targetEmployee, currentCompany]);

  // Name of the resource whose timesheet is being viewed when drilling in from a
  // report. Prefer the employees list, falling back to the timesheet's own
  // employeeName so the banner always has a value.
  const viewedResourceName = useMemo(() => {
    if (!isViewingOtherResource) return null;
    const match = employees.find(e => e.id === targetEmployeeId);
    return match?.name ?? currentTimesheet.employeeName ?? null;
  }, [isViewingOtherResource, employees, targetEmployeeId, currentTimesheet.employeeName]);

  return { currentWeekStart, setCurrentWeekStart, currentTimesheet, viewedResourceName, isRefreshingTimesheets };
}
